import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Camera, Play, X, Check, Upload, Wand2, Video } from 'lucide-react';
function CountUp({
  end,
  suffix = ''
}: {
  end: number;
  suffix?: string;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (isInView) {
      let start = 0;
      const duration = 1500;
      const increment = end / (duration / 16);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      return () => clearInterval(timer);
    }
  }, [isInView, end]);
  return <span ref={ref} data-id="element-188">
      {count}
      {suffix}
    </span>;
}
export function ProblemSolution() {
  return <div className="relative" data-id="element-189">
      {/* Problem Section */}
      <section className="py-[64px] md:py-[96px] bg-bg-secondary" data-id="element-190">
        <div className="container mx-auto px-5 max-w-[1100px]" data-id="element-191">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-center mb-16" data-id="element-192">
            <h2 className="text-[32px] md:text-[48px] font-[900] mb-4 text-white uppercase tracking-tight leading-[1.1]" data-id="element-193">
              EL PROBLEMA NO ES TU PROPIEDAD.
              <br data-id="element-194" />
              <span className="text-accent" data-id="element-195">ES CÓMO LA ESTÁS MOSTRANDO.</span>
            </h2>
            <p className="text-text-secondary text-[18px] font-[500]" data-id="element-196">
              Mientras publicas una foto estática, tu competencia publica un
              video que detiene el scroll y genera consultas.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-[24px] mb-16" data-id="element-197">
            {/* Left Card */}
            <motion.div initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            ease: [0.16, 1, 0.3, 1]
          }} className="bg-[#0F0A0A] border border-[rgba(244,63,94,0.25)] rounded-[16px] p-[32px] hover:-translate-y-[6px] hover:shadow-[0_12px_40px_rgba(244,63,94,0.05)] transition-transform duration-250 flex flex-col" data-id="element-198">
              <div className="inline-block bg-[rgba(244,63,94,0.1)] text-[#F43F5E] rounded-[8px] px-3 py-1 text-[12px] font-bold mb-8 uppercase tracking-[0.08em] self-start" data-id="element-199">
                ✗ PUBLICACIÓN TÍPICA
              </div>
              <div className="bg-[#1A1010] rounded-[8px] aspect-video flex flex-col items-center justify-center mb-8 border border-[#2A1A1A] relative overflow-hidden flex-shrink-0" data-id="element-200">
                <img src="https://cdn.magicpatterns.com/uploads/biwvuFqZDaV6Wzw9WJQ53g/imagem-estattica-imoblabs-1024x1024.png" alt="Foto estática" className="absolute inset-0 w-full h-full object-cover opacity-60 grayscale" data-id="element-201" />
                <div className="absolute inset-0 bg-bg-primary/50" data-id="element-202" />
                <div className="relative z-10 flex flex-col items-center" data-id="element-203">
                  <Camera className="w-8 h-8 text-gray-400 mb-2" data-id="element-204" />
                  <span className="text-gray-400 text-[14px] font-bold bg-bg-primary/80 px-3 py-1 rounded-[4px]" data-id="element-205">
                    Foto estática
                  </span>
                </div>
              </div>
              <div className="space-y-4 mt-auto" data-id="element-206">
                {['Alcance orgánico limitado', 'Atención promedio: 0.8 segundos', 'Se pierde entre publicaciones iguales', 'Pocas consultas recibidas'].map((item, i) => <div key={i} className="flex items-center gap-3" data-id="element-207">
                    <X className="w-5 h-5 text-danger shrink-0" data-id="element-208" />
                    <span className="text-text-secondary text-[15px] leading-[2]" data-id="element-209">
                      {item}
                    </span>
                  </div>)}
              </div>
            </motion.div>

            {/* Right Card */}
            <motion.div initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1,
            ease: [0.16, 1, 0.3, 1]
          }} className="bg-[#070D14] border border-accent-border shadow-[0_0_40px_rgba(0,180,230,0.08)] rounded-[16px] p-[32px] hover:-translate-y-[6px] hover:shadow-[0_12px_40px_rgba(0,180,230,0.15)] transition-transform duration-250 flex flex-col" data-id="element-210">
              <div className="inline-block bg-accent-glow text-accent rounded-[8px] px-3 py-1 text-[12px] font-bold mb-8 uppercase animate-glowPulse self-start" data-id="element-211">
                ✦ VIDEO CON IA
              </div>
              <div className="bg-gradient-to-b from-[#070D14] to-[#0B1829] rounded-[8px] aspect-video flex flex-col items-center justify-center mb-8 border border-accent-border relative overflow-hidden flex-shrink-0" data-id="element-212">
                <video src="https://cdn.magicpatterns.com/uploads/qcyLimVjw3zgW2BLEprdmm/Imob Labs IA - RA Films _ Cursos-1.mov" autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover opacity-90" data-id="element-213" />
                <div className="absolute inset-0 bg-gradient-to-t from-bg-primary/80 via-transparent to-transparent" data-id="element-214" />
                <div className="relative z-10 flex flex-col items-center mt-auto mb-4" data-id="element-215">
                  <Play className="w-8 h-8 text-accent mb-2 animate-glowPulse drop-shadow-[0_0_8px_rgba(0,180,230,0.8)]" data-id="element-216" />
                  <span className="text-accent text-[14px] font-bold bg-bg-primary/80 px-3 py-1 rounded-[4px] border border-accent-border" data-id="element-217">
                    Video cinematográfico IA
                  </span>
                </div>
              </div>
              <div className="space-y-4 mt-auto" data-id="element-218">
                {['Alcance orgánico 3x mayor', 'Atención promedio: 8 a 15 segundos', 'Destaca entre 200 publicaciones', 'Consultas se duplican'].map((item, i) => <div key={i} className="flex items-center gap-3" data-id="element-219">
                    <Check className="w-5 h-5 text-success shrink-0" data-id="element-220" />
                    <span className="text-text-primary text-[15px] leading-[2]" data-id="element-221">
                      {item}
                    </span>
                  </div>)}
              </div>
            </motion.div>
          </div>

          {/* Data Bar */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2,
          ease: [0.16, 1, 0.3, 1]
        }} className="bg-bg-primary border-y border-border-subtle p-[40px]" data-id="element-222">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-0 divide-y md:divide-y-0 md:divide-x divide-border-subtle text-center" data-id="element-223">
              <div className="px-4 py-2 md:py-0" data-id="element-224">
                <div className="text-[48px] font-[900] text-accent mb-1 leading-none" data-id="element-225">
                  <CountUp end={3} suffix="x" data-id="element-226" />
                </div>
                <div className="text-text-secondary text-[14px]" data-id="element-227">
                  más alcance orgánico
                </div>
              </div>
              <div className="px-4 py-2 md:py-0" data-id="element-228">
                <div className="text-[48px] font-[900] text-accent mb-1 leading-none" data-id="element-229">
                  8-15 seg
                </div>
                <div className="text-text-secondary text-[14px]" data-id="element-230">
                  de atención promedio
                </div>
              </div>
              <div className="px-4 py-2 md:py-0" data-id="element-231">
                <div className="text-[48px] font-[900] text-accent mb-1 leading-none" data-id="element-232">
                  <CountUp end={2} suffix="x" data-id="element-233" />
                </div>
                <div className="text-text-secondary text-[14px]" data-id="element-234">
                  más consultas recibidas
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it works Section */}
      <section className="py-[64px] md:py-[96px] bg-bg-primary" data-id="element-235">
        <div className="container mx-auto px-5 max-w-[1100px]" data-id="element-236">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-center mb-16" data-id="element-237">
            <h2 className="text-[32px] md:text-[48px] font-[900] uppercase text-white" data-id="element-238">
              3 PASOS. 3 MINUTOS. KIT COMPLETO.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-[24px] relative mb-16" data-id="element-239">
            {/* Connecting line for desktop */}
            <motion.div initial={{
            scaleX: 0
          }} whileInView={{
            scaleX: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 1,
            ease: 'easeOut'
          }} className="hidden md:block absolute top-[44px] left-[15%] right-[15%] h-px border-t-[2px] border-dashed border-border-card origin-left z-0" data-id="element-240" />

            {[{
            icon: Upload,
            title: 'Subes las fotos',
            desc: 'Desde tu celular o computadora.',
            num: '01'
          }, {
            icon: Wand2,
            title: 'Aplicas los prompts',
            desc: 'Usas nuestras plantillas probadas.',
            num: '02'
          }, {
            icon: Video,
            title: 'Obtienes el video',
            desc: 'Listo para publicar y captar clientes.',
            num: '03'
          }].map((step, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: idx * 0.1,
            ease: [0.16, 1, 0.3, 1]
          }} className="relative text-center z-10 flex flex-col items-center" data-id="element-241">
                <div className="text-[88px] font-[100] text-accent/20 leading-none mb-[-20px] select-none" data-id="element-242">
                  {step.num}
                </div>
                <div className="w-16 h-16 bg-bg-primary flex items-center justify-center mb-4 relative z-10" data-id="element-243">
                  <step.icon className="w-[32px] h-[32px] text-accent animate-glowPulse" data-id="element-244" />
                </div>
                <h3 className="text-[20px] font-[700] mb-2 text-white" data-id="element-245">
                  {step.title}
                </h3>
                <p className="text-text-secondary text-[15px] leading-[1.6] max-w-[250px] mx-auto" data-id="element-246">
                  {step.desc}
                </p>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="bg-bg-card border border-accent-border rounded-[12px] p-[24px_32px] text-center max-w-[640px] mx-auto" data-id="element-247">
            <p className="text-[18px] font-[700] text-white mb-1" data-id="element-248">
              ⚡ 3 minutos con VideoInmo AI
            </p>
            <p className="text-text-secondary text-[16px] mb-4" data-id="element-249">
              vs más de 3 horas en Canva para un resultado básico
            </p>
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-[14px] font-medium" data-id="element-250">
              <span className="text-danger" data-id="element-251">
                Antes: 3h en Canva → imagen básica
              </span>
              <span className="hidden md:inline text-border-subtle" data-id="element-252">|</span>
              <span className="text-accent" data-id="element-253">
                Ahora: 3min → video profesional
              </span>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}